import { useCallback, useState } from 'react';

interface Options {
  onPlay?: () => void;
  onComplete?: () => void;
}

/**
 * Controller for Lottie animations
 */
export interface LottieController {
  /**
   * Play the animation (sets it to visible).
   * Loop behavior is controlled by the LottieAnimation component's `loop` prop.
   */
  play: () => void;

  /**
   * Stop the animation (by making it invisible)
   */
  stop: () => void;

  /**
   * Whether the animation is currently visible/intended to be playing
   */
  isPlaying: boolean;
}

/**
 * Hook to create a controller for Lottie animations
 */
export const useLottieController = (options: Options = {}): LottieController => {
  const { onPlay, onComplete } = options;
  const [isPlaying, setIsPlaying] = useState(false);

  const play = useCallback(() => {
    if (isPlaying) return;
    setIsPlaying(true);
    onPlay?.();
  }, [isPlaying, onPlay]);

  const stop = useCallback(() => {
    if (!isPlaying) return;
    setIsPlaying(false);
    onComplete?.();
  }, [isPlaying, onComplete]);

  return {
    play,
    stop,
    isPlaying,
  };
};
